import Paginator from "./paginator";
import Dialog from "components/dialog";

import clsx from "clsx";
import * as questionnairesApi from "api/questionnaires";
import { questionPath } from "utils/questionnaire";

import { useState } from "react";
import { useNotifications } from "hooks/notifications";
import { useRouter } from "next/router";

import {
  QuestionnaireComponentProps,
  PaginationProps
} from "components/questionnaire";
import { QuestionnaireAnswer } from "types/questionnaire_answer";

interface Props {
  questionnaire: QuestionnaireComponentProps;
  paginationInfo: PaginationProps;
  questionnaireAnswer: QuestionnaireAnswer;
  loading?: boolean;
  disabled?: boolean;
  onPaginate: () => void;
  onComplete: () => void;
  onConfirmAnswer: () => void;
  onRevealAnswer: () => void;
}

export default function QuestionnaireActions(props: Props): JSX.Element {
  const { questionnaire, paginationInfo, questionnaireAnswer } = props;
  const { loading, disabled } = props;
  const { onPaginate, onComplete } = props;

  const notAnsweredQuestions =
    (questionnaire?.totalCount || 0) - (questionnaire?.answeredCount || 0);

  const [showNotAnsweredDialog, setShowNotAnsweredDialog] = useState(false);

  const { setNotification } = useNotifications();
  const router = useRouter();

  function handleQuestionnaireCompletion() {
    if ((questionnaire?.answeredCount || 0) === 0)
      return notifyNotAnsweredQuestionnaire();

    if (notAnsweredQuestions > 0) return setShowNotAnsweredDialog(true);

    onComplete();
  }

  function notifyNotAnsweredQuestionnaire() {
    setNotification({
      type: "error",
      title: "Ainda não é possível finalizar",
      text: "Você precisa responder alguma questão antes de finalizar o simulado"
    });
  }

  function goToNotAnsweredQuestion() {
    onPaginate();
    questionnairesApi.show(questionnaire.id).then((response) => {
      const question = response.body.data.answers[0].question;
      setShowNotAnsweredDialog(false);
      router.push(questionPath(question));
    });
  }

  return (
    <>
      <div className="actions flex flex-col w-full">
        <MainAction {...props} onComplete={handleQuestionnaireCompletion} />

        <div className="mt-5">
          <Paginator
            {...paginationInfo}
            disabled={disabled}
            loading={loading}
            onPaginate={onPaginate}
          />
        </div>

        {paginationInfo.nextUrl && questionnaireAnswer && (
          <button
            onClick={handleQuestionnaireCompletion}
            disabled={disabled}
            className={`btn btn-secondary block mt-8 mx-auto fade-loading ${clsx(
              { loading }
            )}`}
          >
            Terminar simulado
          </button>
        )}
      </div>

      {showNotAnsweredDialog && (
        <Dialog
          title="Nem todas as questões foram respondidas"
          confirmText="Finalizar"
          cancelText="Responder questões"
          onConfirm={onComplete}
          onCancel={goToNotAnsweredQuestion}
        >
          <p className="dialog-text">
            {notAnsweredQuestions} questões ficaram sem reposta. Você quer
            finalizar o simulado assim mesmo?
          </p>
        </Dialog>
      )}
    </>
  );
}

function MainAction(props) {
  const { questionnaire, questionnaireAnswer, paginationInfo } = props;
  const { loading, disabled } = props;
  const { onConfirmAnswer, onRevealAnswer, onComplete } = props;

  const buttonClassNames = clsx("btn btn-primary fade-loading block w-full", {
    loading
  });

  if (!questionnaireAnswer) return null;

  if (!questionnaireAnswer?.seenAnswer && !questionnaire?.seriousFrom) {
    return (
      <button
        className={clsx(buttonClassNames, {
          disabled: !questionnaireAnswer?.answer
        })}
        data-disabled={disabled}
        onClick={
          (questionnaireAnswer?.confirmedAt && onRevealAnswer) ||
          onConfirmAnswer
        }
      >
        {(questionnaireAnswer?.confirmedAt && "Ver resposta") ||
          "Confirmar resposta"}
      </button>
    );
  }

  if (paginationInfo.nextUrl) return <></>;

  return (
    <button
      className={buttonClassNames}
      data-disabled={disabled}
      onClick={onComplete}
    >
      Terminar simulado
    </button>
  );
}
